import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { applyPalette, DEFAULT_PALETTE, PALETTES } from '../config/palettes'

const ThemeContext = createContext(null)

export function ThemeProvider({ children }) {
  const [palette, setPalette] = useState(() => {
    return localStorage.getItem('sendroli_palette') || DEFAULT_PALETTE
  })
  const [darkMode, setDarkMode] = useState(() => {
    return document.documentElement.getAttribute('data-theme') === 'dark'
  })

  useEffect(() => {
    applyPalette(palette, darkMode)
    localStorage.setItem('sendroli_palette', palette)
  }, [palette, darkMode])

  // Watch for dark mode changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark'
      setDarkMode(isDark)
    })
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => observer.disconnect()
  }, [])

  // Fetch palette from WebsiteSettings on mount (public endpoint, no auth needed)
  useEffect(() => {
    async function fetchPalette() {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || '/api'
        const res = await fetch(`${apiUrl}/website/settings`)
        if (res.ok) {
          const data = await res.json()
          const settings = data.data || data
          const serverPalette = settings?.themePalette
          if (serverPalette && PALETTES[serverPalette]) {
            setPalette(serverPalette)
          }
        }
      } catch {
        // silently fall back to local storage value
      }
    }
    fetchPalette()
  }, [])

  const changePalette = useCallback((newPalette) => {
    if (PALETTES[newPalette]) {
      setPalette(newPalette)
      applyPalette(newPalette, darkMode)
      localStorage.setItem('sendroli_palette', newPalette)
    }
  }, [darkMode])

  return (
    <ThemeContext.Provider value={{ palette, changePalette, darkMode }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}

export default ThemeContext
